import type {
  ConfigLayer,
  EffectiveConfig,
  EntityDetails,
  HierarchyNodeKind,
  OrgNode,
  RepositoryDetails,
  ProjectSettings,
  TenantProduct,
} from '@stoaria-control/schema'
import { ApiError, apiFetch } from './api-fetch.ts'

/** One record of the tree (an entity — which IS a project —, a server or a
 * repository connection), with everything beneath it. */
export interface TreeNode extends OrgNode {
  children: TreeNode[]
  /** Whether a session can target this node: its resolved project settings
   * name a repository. Computed by the API. */
  launchable: boolean
}

/** A tenant: the root of a tree, and the only node that carries a product
 * entitlement. Children are plain `TreeNode`s — `products` is not inherited
 * and asking a child for it is a bug, so the type does not offer it. */
export interface RootNode extends TreeNode {
  /** Which of Sessions / Vault this tenant has bought. The API ENFORCES
   * this (an unentitled tenant's `/vault/**` and `/sessions**` calls are
   * refused with the same 403 a non-member gets), so hiding navigation on
   * it is cosmetic agreement with the server, never the control itself. */
  products: TenantProduct[]
}

export interface HierarchyTree {
  roots: RootNode[]
}

export function fetchHierarchy(): Promise<HierarchyTree> {
  return apiFetch<HierarchyTree>('/hierarchy')
}

export function fetchEffectiveConfig(nodeId: string): Promise<EffectiveConfig> {
  return apiFetch<EffectiveConfig>(`/nodes/${nodeId}/effective-config`)
}

export function createOrgNode(body: {
  name: string
  parentId: string | null
  kind?: 'entity' | 'server' | 'repository'
  entityType?: string
  hostName?: string
}): Promise<OrgNode> {
  return apiFetch<OrgNode>('/nodes', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/** Every record is an org node now, so the kind only labels the selection;
 * everything saves through `/nodes/{id}`. */
export type PatchKind = HierarchyNodeKind

export interface NodePatchBody {
  name?: string
  entityType?: string
  hostName?: string
  config?: ConfigLayer
  entityDetails?: EntityDetails
  projectSettings?: ProjectSettings
  repositoryDetails?: RepositoryDetails
  defaultBranch?: string
  /** Absent leaves the parent untouched, `null` promotes to top level, a
   * uuid moves the node under it. */
  parentId?: string | null
}

export function patchNode(
  _kind: PatchKind,
  id: string,
  body: NodePatchBody,
): Promise<OrgNode> {
  return apiFetch<OrgNode>(`/nodes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

/** Queues a verification probe for a project node. Returns once QUEUED, not
 * once answered: harnesses are outbound-poll, so the check runs on whichever
 * online node picks it up on its next heartbeat. */
export function verifyNode(nodeId: string): Promise<{ probeId: string }> {
  return apiFetch<{ probeId: string }>(`/nodes/${nodeId}/verify`, {
    method: 'POST',
  })
}

/** Soft-deletes a node. The API refuses (409 `node_has_children`) while it
 * still has live child nodes. */
export function deleteOrgNode(id: string): Promise<void> {
  // `undefined`, not `void`, as the generic: house lint bans void in type
  // argument position; the 204 branch returns undefined either way.
  return apiFetch<undefined>(`/nodes/${id}`, { method: 'DELETE' })
}

// `ApiError` used to be declared here; it now lives in `api-fetch.ts`
// beside the fetch that throws it. Re-exported so existing importers of
// `./hierarchy-api.ts` keep working.
export { ApiError }
